import api from './api';
import type { EmailAttachment } from '@/types';

class AttachmentService {
  async getAttachment(emailId: string, attachmentId: string): Promise<EmailAttachment | null> {
    const response = await api.get<EmailAttachment>(
      `/emails/${emailId}/attachments/${attachmentId}`
    );
    return response.success && response.data ? response.data : null;
  }

  async downloadAttachment(emailId: string, attachmentId: string): Promise<Blob | null> {
    try {
      const axiosInstance = api.getAxiosInstance();
      const response = await axiosInstance.get(
        `/emails/${emailId}/attachments/${attachmentId}`,
        {
          responseType: 'blob',
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error downloading attachment:', error);
      return null;
    }
  }

  /**
   * Baja el adjunto de su copia en Storage (la URL lleva su propio token) y
   * solo sin copia, o si falla, lo pide al backend: este responde 401 a quien
   * no tiene su propio buzón conectado aunque el archivo ya esté guardado.
   */
  async fetchAttachmentBlob(emailId: string, attachment: EmailAttachment): Promise<Blob | null> {
    const storedUrl = attachment.downloadUrl ?? attachment.url;
    if (storedUrl) {
      try {
        const response = await fetch(storedUrl);
        if (response.ok) return await response.blob();
      } catch (error) {
        console.error('Error downloading attachment from Storage:', error);
      }
    }
    return this.downloadAttachment(emailId, attachment.id);
  }

  async getAttachmentBlobUrl(emailId: string, attachmentId: string): Promise<string | null> {
    try {
      const blob = await this.downloadAttachment(emailId, attachmentId);
      if (blob) {
        return window.URL.createObjectURL(blob);
      }
      return null;
    } catch (error) {
      console.error('Error getting attachment blob URL:', error);
      return null;
    }
  }

  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  getFileIcon(contentType: string): string {
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.startsWith('video/')) return 'video';
    if (contentType.startsWith('audio/')) return 'audio';
    if (contentType.includes('pdf')) return 'file-text';
    if (contentType.includes('word') || contentType.includes('document')) return 'file-text';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'file-spreadsheet';
    if (contentType.includes('powerpoint') || contentType.includes('presentation')) return 'file-presentation';
    if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('compressed')) return 'file-archive';
    return 'file';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  isImageFile(contentType: string): boolean {
    return contentType.startsWith('image/');
  }

  isPdfFile(contentType: string): boolean {
    return contentType.includes('pdf');
  }

  isExcelFile(contentType: string, filename?: string): boolean {
    const excelMimes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    if (excelMimes.includes(contentType)) return true;
    const ext = filename?.toLowerCase().split('.').pop() ?? '';
    return ['xlsx', 'xls', 'csv'].includes(ext);
  }

  /**
   * Un correo adjunto dentro de otro. Se mira también la extensión porque
   * muchos remitentes mandan el .eml como application/octet-stream.
   */
  isEmailFile(contentType: string, filename?: string): boolean {
    if (contentType === 'message/rfc822') return true;
    const ext = filename?.toLowerCase().split('.').pop() ?? '';
    return ext === 'eml';
  }

  canPreview(contentType: string, filename?: string): boolean {
    return this.isImageFile(contentType)
      || this.isPdfFile(contentType)
      || this.isExcelFile(contentType, filename)
      || this.isEmailFile(contentType, filename);
  }
}

export default new AttachmentService();
